import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Video,
  Mic,
  Sparkles,
  ArrowRight,
  Check,
  X,
  ShieldCheck,
  Radio,
  Heart,
  Music,
  Camera,
  Building2,
  Briefcase,
  HandHeart,
  Megaphone,
  Calendar,
  Users,
  PlayCircle,
  Award,
  ChevronDown,
  Sparkle,
  Tv,
  Star,
  BookOpen,
  Store,
  Church,
  PartyPopper,
  ShoppingBag,
  GraduationCap,
} from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import SeoMeta from "@/components/SeoMeta";

export default function OnlyBestStreamersPage() {
  return (
    <div className="min-h-screen bg-[#070B16] text-white" data-testid="onlybest-streamers-page">
      <SeoMeta
        title="Become an OnlyBest Streamer | LocalBest.ai"
        description="Go live for local businesses, professionals, charities, churches, artists, musicians, actors, models, startups, events, and community campaigns — and help trusted businesses get seen, followed, booked, supported, and sponsored."
        canonical="https://localbest.ai/onlybest-streamers"
      />

      <Hero />
      <WhatIs />
      <WhatYouStream />
      <HowItWorks />
      <WhatWeProvide />
      <WhoFor />
      <WhoNotFor />
      <Integrity />
      <BusinessSideCTA />
      <Faq />
      <FinalCTA />
    </div>
  );
}

/* ---------------- HERO ---------------- */
function Hero() {
  return (
    <section className="relative pt-24 pb-16 overflow-hidden border-b border-white/5">
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_25%_15%,rgba(220,38,38,0.18),transparent_55%),radial-gradient(circle_at_75%_70%,rgba(37,99,235,0.18),transparent_55%)]" />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <Link
          to="/onlybest"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.3em] text-[#DC2626] hover:text-white transition mb-6"
          data-testid="streamers-back-link"
        >
          ← Back to OnlyBest Creators
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-[#DC2626]/15 to-[#2563EB]/15 border border-[#DC2626]/30 mb-6"
        >
          <Radio className="w-4 h-4 text-[#DC2626] animate-pulse" />
          <span className="text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] text-[#DC2626]">
            OnlyBest Streamers
          </span>
        </motion.div>

        <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold font-['Outfit'] leading-[1.04] tracking-tight">
          Become an{" "}
          <span className="bg-gradient-to-r from-[#DC2626] via-[#F59E0B] to-[#2563EB] bg-clip-text text-transparent">
            OnlyBest Streamer
          </span>
        </h1>

        <p className="mt-6 text-base sm:text-lg text-[#CBD5E1] max-w-3xl mx-auto leading-relaxed">
          Go live for local businesses, events, charities, churches, artists,
          musicians, professionals, startups, and community campaigns — and
          help trusted businesses get seen, followed, booked, supported, and
          sponsored.
        </p>

        <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
          <Link to="/onlybest/apply">
            <Button
              size="lg"
              className="bg-gradient-to-r from-[#DC2626] to-[#2563EB] hover:opacity-90 text-white font-semibold rounded-full px-8 py-6 text-base shadow-lg shadow-[#DC2626]/30"
              data-testid="streamers-primary-cta"
            >
              <Sparkles className="w-4 h-4 mr-2" /> Join Free as an OnlyBest Creator
            </Button>
          </Link>
          <a href="#how-it-works">
            <Button
              size="lg"
              variant="outline"
              className="border-white/20 bg-white/5 hover:bg-white/10 text-white rounded-full px-7 py-6 text-base"
              data-testid="streamers-secondary-cta"
            >
              <PlayCircle className="w-4 h-4 mr-2" /> See How Streaming Works
            </Button>
          </a>
        </div>

        <p className="mt-5 text-xs sm:text-sm text-[#64748B] max-w-xl mx-auto leading-relaxed">
          OnlyBest is free to join. Your AI Horse-Powered Systems access
          activates when you have at least one active referred or assigned
          business client.
        </p>
      </div>
    </section>
  );
}

/* ---------------- WHAT IS ---------------- */
function WhatIs() {
  return (
    <section className="py-20 border-b border-white/5">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <SimpleHeading
          kicker="What Is an OnlyBest Streamer?"
          title="A live-video ambassador for the businesses and causes you believe in."
        />
        <p className="mt-8 text-base sm:text-lg text-[#CBD5E1] leading-relaxed text-center">
          An OnlyBest Streamer is an approved creator, host, presenter,
          interviewer, live seller, event promoter, or community ambassador who
          uses live video to help trusted local businesses and community
          partners grow. Streams can be published from the LocalBest.ai
          dashboard and shown on the business listing,{" "}
          <span className="text-white font-semibold">LocalBest.ai/live</span>,
          and other approved LocalBest.ai placements.
        </p>
      </div>
    </section>
  );
}

/* ---------------- WHAT YOU CAN STREAM ---------------- */
function WhatYouStream() {
  const items = [
    { icon: Store, label: "Local business spotlights" },
    { icon: Mic, label: "Owner interviews" },
    { icon: Video, label: "Product or service demos" },
    { icon: ShoppingBag, label: "Live shopping & offers" },
    { icon: HandHeart, label: "Charity & fundraiser streams" },
    { icon: Church, label: "Church & community events" },
    { icon: Music, label: "Artist & musician showcases" },
    { icon: Camera, label: "Actor, model & talent showcases" },
    { icon: Briefcase, label: "Startup pitch streams" },
    { icon: Megaphone, label: "Affiliate & sponsorship campaigns" },
    { icon: Star, label: "Best of Best LIVE event segments" },
    { icon: Calendar, label: "Local event coverage" },
    { icon: BookOpen, label: "Podcast-style interviews" },
    { icon: GraduationCap, label: "Educational workshops" },
    { icon: PartyPopper, label: "Grand openings & promotions" },
    { icon: Tv, label: "Weekly/monthly sponsored shows" },
  ];
  return (
    <section className="py-20 border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SimpleHeading
          kicker="What Can You Stream or Promote?"
          title="If it deserves attention with integrity, you can stream it."
        />
        <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <div
                key={it.label}
                className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 hover:border-[#DC2626]/40 hover:bg-white/[0.05] transition flex items-center gap-3 min-w-0"
              >
                <span className="inline-flex w-9 h-9 rounded-lg bg-gradient-to-br from-[#DC2626]/20 to-[#2563EB]/20 items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-[#F59E0B]" />
                </span>
                <span className="text-sm text-[#E2E8F0] leading-snug">
                  {it.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------------- HOW IT WORKS ---------------- */
function HowItWorks() {
  const steps = [
    {
      n: 1,
      title: "Join OnlyBest free",
      body: "Apply on /onlybest/apply. Set your password. Open your Creator dashboard.",
    },
    {
      n: 2,
      title: "Land your first active client",
      body: "Refer a local business or get assigned to one. AI Horse-Powered Systems access activates the moment that client goes active.",
    },
    {
      n: 3,
      title: "Create a live event in the dashboard",
      body: "Use the LocalBest.ai dashboard to schedule and configure a stream or live event for your client.",
    },
    {
      n: 4,
      title: "Publish to listing + LocalBest.ai/live",
      body: "The stream publishes to the business listing, LocalBest.ai/live, and any approved LocalBest.ai placement.",
    },
    {
      n: 5,
      title: "Convert viewers into action",
      body: "Viewers can claim offers, book appointments, donate, register, buy, chat with Nox, or contact the business — right from the stream.",
    },
  ];
  return (
    <section id="how-it-works" className="py-20 border-b border-white/5 scroll-mt-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <SimpleHeading
          kicker="How It Works"
          title="Five simple steps from sign-up to monetized live broadcasts."
        />
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-5 gap-5">
          {steps.map((s) => (
            <div
              key={s.n}
              className="relative rounded-2xl border border-white/10 bg-gradient-to-br from-[#0F172A] to-[#0B1226] p-6 min-w-0"
            >
              <span className="inline-flex w-9 h-9 rounded-full bg-gradient-to-br from-[#DC2626] to-[#2563EB] items-center justify-center text-white font-bold text-sm mb-4">
                {s.n}
              </span>
              <h3 className="text-white font-semibold text-base mb-2 leading-snug">
                {s.title}
              </h3>
              <p className="text-sm text-[#94A3B8] leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- WHAT WE PROVIDE ---------------- */
function WhatWeProvide() {
  const items = [
    "LocalBest.ai creator / business listing",
    "Nox AI Sales Agent support",
    "AI Horse-Powered Systems access after one active client",
    "Dashboard publishing tools",
    "Business order flow",
    "CRM lead capture",
    "AI assistants",
    "VA POD team support",
    "Stream/event publishing to listing and /live",
    "Offer, booking, donation, registration & lead capture CTAs",
    "Reporting and campaign tracking",
  ];
  return (
    <section className="py-20 border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SimpleHeading
          kicker="What LocalBest.ai Provides"
          title="You are not alone — the platform does the heavy lifting with you."
        />
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((it) => (
            <div
              key={it}
              className="flex items-start gap-3 rounded-xl bg-white/[0.03] border border-white/10 p-4"
            >
              <Sparkle className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
              <span className="text-sm text-[#E2E8F0] leading-relaxed">{it}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- WHO IS THIS FOR ---------------- */
function WhoFor() {
  const items = [
    "Streamers",
    "Hosts",
    "Interviewers",
    "Podcasters",
    "Influencers",
    "Local ambassadors",
    "Event promoters",
    "Church / community media helpers",
    "Artists & musicians",
    "Affiliate marketers",
    "Live sellers",
    "Content creators",
    "Students, parents, veterans, retirees, side-hustlers",
    "Anyone who can communicate, promote, and represent businesses with integrity",
  ];
  return (
    <section className="py-20 border-b border-white/5">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <SimpleHeading
          kicker="Who This Is For"
          title="You don't need to be famous. You need to be real."
        />
        <div className="mt-12 flex flex-wrap gap-2 justify-center">
          {items.map((w) => (
            <span
              key={w}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/[0.04] border border-white/10 text-sm text-[#E2E8F0]"
            >
              <Check className="w-3.5 h-3.5 text-[#10B981]" /> {w}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- WHO THIS IS NOT FOR ---------------- */
function WhoNotFor() {
  const items = [
    "Scammers",
    "Fake experts",
    "People using fake followers, fake views, or fake engagement",
    "Anyone making misleading claims",
    "Anyone stealing content",
    "Anyone using unauthorized music, celebrity likenesses, or voices",
    "Anyone trying to take LocalBest.ai clients off-platform",
    "Anyone unwilling to disclose sponsorships or affiliate relationships",
    "Anyone unwilling to represent businesses professionally",
  ];
  return (
    <section className="py-20 border-b border-white/5">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <SimpleHeading
          kicker="Who This Is Not For"
          title="We protect the trust of every business on LocalBest.ai."
        />
        <div className="mt-12 grid sm:grid-cols-2 gap-3">
          {items.map((w) => (
            <div
              key={w}
              className="flex items-start gap-3 rounded-xl bg-[#7F1D1D]/10 border border-[#DC2626]/20 p-4"
            >
              <X className="w-4 h-4 text-[#DC2626] flex-shrink-0 mt-0.5" />
              <span className="text-sm text-[#CBD5E1] leading-relaxed">{w}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- INTEGRITY ---------------- */
function Integrity() {
  return (
    <section className="py-20 border-b border-white/5">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="rounded-3xl border border-[#10B981]/30 bg-gradient-to-br from-[#10B981]/8 via-[#0F172A] to-[#2563EB]/8 p-8 sm:p-10">
          <div className="flex items-center gap-3 mb-5">
            <span className="inline-flex w-12 h-12 rounded-2xl bg-gradient-to-br from-[#10B981] to-[#2563EB] items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" />
            </span>
            <h3 className="text-2xl sm:text-3xl font-bold text-white font-['Outfit']">
              Trust &amp; Integrity Rules
            </h3>
          </div>
          <p className="text-[#CBD5E1] text-base leading-relaxed">
            OnlyBest Streamers must promote with integrity. Sponsored streams,
            paid partnerships, affiliate links, and commissions must be clearly
            disclosed. Streamers must follow platform rules, LocalBest.ai
            policies, copyright rules, and business approval standards.
            LocalBest.ai may remove creators for fraud, fake claims, poor
            delivery, unethical conduct, or off-platform client poaching.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ---------------- BUSINESS SIDE CTA ---------------- */
function BusinessSideCTA() {
  return (
    <section className="py-20 border-b border-white/5">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="rounded-3xl border border-[#F59E0B]/30 bg-gradient-to-br from-[#F59E0B]/10 via-[#0F172A] to-[#EC4899]/10 p-8 sm:p-12">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-8">
            <span className="inline-flex w-14 h-14 rounded-2xl bg-gradient-to-br from-[#F59E0B] to-[#EC4899] items-center justify-center flex-shrink-0">
              <Building2 className="w-6 h-6 text-white" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-[0.25em] text-[#F59E0B] mb-2">
                For Businesses
              </p>
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white font-['Outfit'] mb-4 leading-tight">
                Need a Streamer for Your Business or Event?
              </h3>
              <p className="text-[#CBD5E1] leading-relaxed mb-6">
                Businesses listed on LocalBest.ai can use their dashboard to
                request live event support, business showcases, interviews,
                product demos, charity streams, sponsored segments, and
                recurring promotional content from their assigned OnlyBest
                Creator or approved OnlyBest Streamer.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/dashboard">
                  <Button
                    size="lg"
                    className="bg-gradient-to-r from-[#F59E0B] to-[#EC4899] hover:opacity-90 text-white font-semibold rounded-full px-7 py-5"
                    data-testid="business-cta-ask-creator"
                  >
                    <Mic className="w-4 h-4 mr-2" /> Ask Your OnlyBest Creator
                  </Button>
                </Link>
                <Link to="/dashboard">
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-white/20 bg-white/5 hover:bg-white/10 text-white rounded-full px-7 py-5"
                    data-testid="business-cta-request-streaming"
                  >
                    Request Streaming Support
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- FAQ ---------------- */
function Faq() {
  const faqs = [
    {
      q: "Does it cost to join?",
      a: "No. OnlyBest is free to join.",
    },
    {
      q: "When do I get access to the AI Horse-Powered Systems?",
      a: "Access activates when you have at least one active referred or assigned business client.",
    },
    {
      q: "Do I need a huge audience?",
      a: "No. Local trust, communication skills, consistency, and professionalism matter. A small engaged audience can still help a business.",
    },
    {
      q: "Where do streams appear?",
      a: "Streams can appear on the business listing, LocalBest.ai/live, and other approved LocalBest.ai placements.",
    },
    {
      q: "Can I use YouTube, Facebook, Zoom, StreamYard, Vimeo, TikTok Live, Twitch, Kick, or a custom link?",
      a: "Yes. The dashboard supports current live event publishing options and is expandable to include Twitch, Kick, LinkedIn Live, Rumble, Amazon Live, Whatnot, Instagram Live/link, and custom embeds.",
    },
    {
      q: "Can businesses hire me monthly?",
      a: "Yes. Businesses may hire approved creators for one-time campaigns or recurring monthly support.",
    },
    {
      q: "Can I promote charities, churches, artists, musicians, models, actors, startups, and affiliates?",
      a: "Yes, as long as the campaign is approved, compliant, and aligned with LocalBest.ai standards.",
    },
    {
      q: "Do I have to be on camera?",
      a: "Not always. Some creators may host, produce, moderate chat, edit clips, manage event pages, support Nox, or help publish and promote the event.",
    },
    {
      q: "Can I sell services outside LocalBest.ai?",
      a: "OnlyBest-related client work should be ordered and tracked through LocalBest.ai to protect payments, reporting, quality control, and support.",
    },
    {
      q: "Where can I learn about earnings?",
      a: "The main OnlyBest Creator page explains the current referral, service, and creator compensation model.",
    },
  ];
  const [open, setOpen] = useState(null);
  return (
    <section className="py-20 border-b border-white/5">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <SimpleHeading kicker="FAQ" title="Quick answers." />
        <div className="mt-10 space-y-3">
          {faqs.map((f, idx) => {
            const isOpen = open === idx;
            return (
              <div
                key={f.q}
                className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : idx)}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-white/[0.04] transition"
                  data-testid={`streamer-faq-toggle-${idx}`}
                >
                  <span className="text-white font-medium text-sm sm:text-base">
                    {f.q}
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 text-[#94A3B8] flex-shrink-0 transition-transform ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {isOpen && (
                  <div className="px-5 pb-4 text-sm text-[#CBD5E1] leading-relaxed">
                    {f.a}
                    {idx === 9 && (
                      <>
                        {" "}
                        <Link
                          to="/onlybest"
                          className="text-[#F59E0B] hover:underline"
                        >
                          See compensation details →
                        </Link>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------------- FINAL CTA ---------------- */
function FinalCTA() {
  return (
    <section className="py-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <Award className="w-10 h-10 text-[#F59E0B] mx-auto mb-5" />
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white font-['Outfit'] leading-tight tracking-tight">
          The mic is open. The platform is ready.{" "}
          <br className="hidden sm:block" /> Go help your community win.
        </h2>
        <p className="mt-5 text-base sm:text-lg text-[#94A3B8] max-w-2xl mx-auto leading-relaxed">
          Apply free as an OnlyBest Creator and start streaming for the
          businesses, causes, and communities you believe in.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link to="/onlybest/apply">
            <Button
              size="lg"
              className="bg-gradient-to-r from-[#DC2626] to-[#2563EB] hover:opacity-90 text-white font-semibold rounded-full px-8 py-6 text-base shadow-lg shadow-[#DC2626]/30"
              data-testid="streamers-final-cta"
            >
              <Sparkles className="w-4 h-4 mr-2" /> Join Free as an OnlyBest Creator
            </Button>
          </Link>
          <Link to="/onlybest">
            <Button
              size="lg"
              variant="outline"
              className="border-white/20 bg-white/5 hover:bg-white/10 text-white rounded-full px-7 py-6 text-base"
              data-testid="streamers-back-to-onlybest"
            >
              See How Creators Earn
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
        <p className="mt-6 text-xs text-[#64748B] flex items-center justify-center gap-2">
          <Users className="w-3.5 h-3.5" />
          Free to join · AI Horse-Powered Systems unlock with 1 active client
        </p>
      </div>
    </section>
  );
}

/* ---------------- SHARED HEADING ---------------- */
function SimpleHeading({ kicker, title }) {
  return (
    <div className="text-center max-w-3xl mx-auto">
      <p className="text-xs sm:text-sm font-bold tracking-[0.25em] text-[#DC2626] uppercase mb-3">
        {kicker}
      </p>
      <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white font-['Outfit'] leading-tight tracking-tight">
        {title}
      </h2>
    </div>
  );
}
